import { useState, useEffect, useCallback, useRef } from 'react';
import { LanguageCode } from '../types';
import { streamSpeech } from '../services/geminiService';

const LANGUAGE_MAP: Record<LanguageCode, string> = {
  [LanguageCode.EN]: 'en-ZA',
  [LanguageCode.ZU]: 'zu-ZA',
  [LanguageCode.XH]: 'xh-ZA',
  [LanguageCode.TN]: 'tn-ZA',
  [LanguageCode.AF]: 'af-ZA',
  [LanguageCode.TS]: 'ts-ZA',
};

// Helper to decode Base64 to ArrayBuffer
function base64ToArrayBuffer(base64: string) {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}

// Clean text for speech processing
function cleanTextForSpeech(text: string): string {
    return text
        .replace(/\*\*/g, '')      // Bold
        .replace(/###/g, '')       // Headers
        .replace(/---/g, '')       // HR
        .replace(/\[.*?\]/g, '')   // Brackets
        .replace(/^\s*[-•]\s*/gm, '') // List bullets
        .replace(/\s+/g, ' ')      // Normalize whitespace
        .trim();
}

// Split text into meaningful chunks (sentences) to speed up initial playback
function splitTextIntoChunks(text: string): string[] {
    const cleaned = cleanTextForSpeech(text);
    if (!cleaned) return [];

    // Split by sentence endings (. ! ?) followed by space or end of string
    // This regex looks for punctuation that ends a sentence
    const matches = cleaned.match(/[^.!?]+[.!?]+(\s|$)|[^.!?]+$/g);
    
    if (!matches) return [cleaned];

    const chunks: string[] = [];
    let currentChunk = '';

    for (let i = 0; i < matches.length; i++) {
        const sentence = matches[i];
        
        // OPTIMIZATION:
        // The first chunk should be very small (~80 chars) to ensure the first API call 
        // returns bytes almost immediately (Low Time-To-First-Byte).
        // Subsequent chunks can be larger (~300 chars) for better network efficiency.
        const targetLimit = chunks.length === 0 ? 80 : 300;

        if ((currentChunk + sentence).length < targetLimit) {
            currentChunk += sentence;
        } else {
            if (currentChunk.trim()) chunks.push(currentChunk.trim());
            currentChunk = sentence;
        }
    }
    if (currentChunk.trim()) chunks.push(currentChunk.trim());

    return chunks;
}

export const useVoice = (language: LanguageCode, enableMic: boolean = true) => {
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [voiceError, setVoiceError] = useState<string | null>(null);
  
  const recognitionRef = useRef<any>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  // Track all active sources for cancellation
  const sourcesRef = useRef<AudioBufferSourceNode[]>([]);
  // Flag to handle cancellation in async loop
  const isCancelledRef = useRef(false);

  // Initialize Speech Recognition (Input)
  useEffect(() => {
    if (!enableMic) return;
    
    if (typeof window !== 'undefined') {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      
      if (SpeechRecognition) {
        const recognitionInstance = new SpeechRecognition();
        recognitionInstance.continuous = false;
        recognitionInstance.interimResults = false;
        recognitionInstance.lang = LANGUAGE_MAP[language];
        
        recognitionInstance.onstart = () => {
            setIsListening(true);
            setVoiceError(null);
        };
        
        recognitionInstance.onend = () => {
            setIsListening(false);
        };
        
        recognitionInstance.onresult = (event: any) => {
          const text = event.results[0][0].transcript;
          if (text) {
              setTranscript(text);
          }
        };
        
        recognitionInstance.onerror = (event: any) => {
            console.error("Speech Recognition Error:", event.error);
            setIsListening(false);
            if (event.error === 'not-allowed') {
                setVoiceError('Microphone access denied.');
            } else if (event.error === 'no-speech') {
                 setVoiceError('No speech detected.');
            } else if (event.error === 'network') {
                 setVoiceError('Network error during voice input.');
            } else {
                setVoiceError('Voice input error. Try again.');
            }
        };
        
        recognitionRef.current = recognitionInstance;
      } else {
        setVoiceError('Voice input not supported in this browser.');
      }
    }

    return () => {
        if (recognitionRef.current) {
            try { recognitionRef.current.abort(); } catch (e) {}
        }
        stopAudio();
    };
  }, [language, enableMic]);

  const startListening = useCallback(() => {
    setVoiceError(null);
    stopAudio(); // Stop speaking if listening starts
    
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
         alert("Voice input is not supported in this browser. Please use Chrome, Edge, or Safari.");
         return;
    }

    if (recognitionRef.current) {
      try {
        recognitionRef.current.start();
      } catch (e) {
        console.warn("Speech recognition already started or failed:", e);
      }
    }
  }, []);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  }, []);

  // Audio Playback Control
  const stopAudio = useCallback(() => {
      // Signal async loop to stop
      isCancelledRef.current = true;

      // Stop all currently scheduled sources
      if (sourcesRef.current.length > 0) {
          sourcesRef.current.forEach(source => {
              try { source.stop(); } catch (e) {}
          });
          sourcesRef.current = [];
      }
      
      setIsSpeaking(false);
  }, []);

  const speakText = useCallback(async (text: string) => {
    if (!text) return;

    if (isSpeaking) {
        stopAudio();
        // Allow a microtask for cleanup to propagate
        await new Promise(resolve => setTimeout(resolve, 0));
    }

    setIsSpeaking(true);
    isCancelledRef.current = false;
    sourcesRef.current = [];

    try {
        // 1. Setup Audio Context
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ 
                sampleRate: 24000 // Gemini TTS usually returns 24kHz
            });
        }
        const ctx = audioContextRef.current;
        
        if (ctx.state === 'suspended') {
            await ctx.resume();
        }

        // Initialize scheduling time
        let nextStartTime = ctx.currentTime + 0.1;

        // 2. Prepare chunks for streaming
        // Splitting text allows us to start playing the first sentence immediately
        // rather than waiting for the whole paragraph to be generated.
        const chunks = splitTextIntoChunks(text);

        // 3. Process chunks sequentially
        // Note: Sequential processing here is fast enough because scheduling (download+decode)
        // is much faster than playback. By the time chunk 1 finishes playing, chunk 2 is long ready.
        for (const chunkText of chunks) {
            if (isCancelledRef.current) break;

            const stream = streamSpeech(chunkText, language);

            for await (const chunk of stream) {
                if (isCancelledRef.current) break;

                // Decode Chunk (PCM Int16 -> Float32)
                const arrayBuffer = base64ToArrayBuffer(chunk);
                const dataInt16 = new Int16Array(arrayBuffer);
                const float32Data = new Float32Array(dataInt16.length);
                
                for (let i = 0; i < dataInt16.length; i++) {
                    float32Data[i] = dataInt16[i] / 32768.0;
                }

                const audioBuffer = ctx.createBuffer(1, float32Data.length, 24000);
                audioBuffer.getChannelData(0).set(float32Data);

                // Schedule Playback
                const source = ctx.createBufferSource();
                source.buffer = audioBuffer;
                source.connect(ctx.destination);
                
                // Ensure playback doesn't drift into the past
                const scheduleTime = Math.max(ctx.currentTime, nextStartTime);
                source.start(scheduleTime);
                
                // Track source
                sourcesRef.current.push(source);
                source.onended = () => {
                    sourcesRef.current = sourcesRef.current.filter(s => s !== source);
                };

                // Advance time cursor
                nextStartTime = scheduleTime + audioBuffer.duration;
            }
        }

        // Handle end of stream state
        if (!isCancelledRef.current) {
            const remainingTime = (nextStartTime - ctx.currentTime) * 1000;
            if (remainingTime > 0) {
                setTimeout(() => {
                    if (!isCancelledRef.current) setIsSpeaking(false);
                }, remainingTime);
            } else {
                 setIsSpeaking(false);
            }
        }

    } catch (error) {
        console.error("TTS Error:", error);
        setIsSpeaking(false);
        setVoiceError("Failed to stream audio.");
    }
  }, [language, isSpeaking, stopAudio]);

  const cancelSpeech = useCallback(() => {
      stopAudio();
  }, [stopAudio]);

  return {
    isListening,
    transcript,
    setTranscript,
    startListening,
    stopListening,
    isSpeaking,
    speakText,
    cancelSpeech,
    voiceError
  };
};